import { lazy, Suspense, useEffect } from "react";
import { useMobileViewport } from "hooks/useMobileViewport";
import { preloadEmbeddedComponentNamespaces } from "i18n";

const loadDesktopObligationDetails = () => import("./ObligationDetails");
const loadMobileObligationDetails = () => import("./MobileObligationDetails");

const ObligationDetails = lazy(loadDesktopObligationDetails);
const MobileObligationDetails = lazy(loadMobileObligationDetails);

/**
 * Switch entry point that selects the desktop or mobile implementation of
 * ObligationDetails based on:
 *   - the explicit `isMobile` prop callers may pass
 *   - useMobileViewport().isMobileViewport — true when (a) the client is a phone
 *     (UA / Client Hints) and the viewport is phone-class sized, or (b) both mobile
 *     width and height breakpoints match (same idea as the legacy shell flag).
 *
 * Both implementations expose the same public API:
 *   obligationId, recurrenceId, displayMode, drawerTriggerText, drawerTriggerTextStyle,
 *   drawerWidth, customOpenTriggerRender, suppressDrawerTriggerTooltip,
 *   hasAccess, readOnly, isMobile, open, onClose, onOpen, onDataChange,
 *   onStatusChange.
 *
 * Code-splitting and Suspense behavior are preserved.
 */
const LazyObligationDetails = (props) => {
  const { isMobileViewport } = useMobileViewport();
  const useMobile = props.isMobile || isMobileViewport;

  useEffect(() => {
    void preloadEmbeddedComponentNamespaces();
  }, []);

  return (
    <Suspense fallback={null}>
      {useMobile
        ? <MobileObligationDetails {...props} />
        : <ObligationDetails {...props} />}
    </Suspense>
  );
};

export default LazyObligationDetails;
